import { createContext, useContext, useState, type ReactNode } from "react";

type ToolType = "dragonball" | "sparrow" | null;

interface DeviceStatusInfo {
  toolId: string;
  firmware: string;
  battery: number;
  storageGB: number;
  signalStrength: number;
  temperature: number;
  isConnected: boolean;
  testPassed: boolean | null;
  isRecording: boolean;
  runtimeSeconds: number;
}

export interface ToolContextValue {
  selectedTool: ToolType;
  setSelectedTool: (tool: ToolType) => void;
  connectedTool: string | null;
  setConnectedTool: (tool: string | null) => void;
  deviceStatus: DeviceStatusInfo | null;
  setDeviceStatus: (status: DeviceStatusInfo | null) => void;
}

const ToolContext = createContext<ToolContextValue>({
  selectedTool: null,
  setSelectedTool: () => {},
  connectedTool: null,
  setConnectedTool: () => {},
  deviceStatus: null,
  setDeviceStatus: () => {},
});

export function ToolProvider({ children }: { children: ReactNode }) {
  const [selectedTool, setSelectedTool] = useState<ToolType>(
    () => (localStorage.getItem("sparrow-selected-tool") as ToolType) || null,
  );
  const [connectedTool, setConnectedTool] = useState<string | null>(null);
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatusInfo | null>(null);

  const handleSetTool = (tool: ToolType) => {
    if (tool) {
      localStorage.setItem("sparrow-selected-tool", tool);
    } else {
      localStorage.removeItem("sparrow-selected-tool");
    }
    setSelectedTool(tool);
  };

  return (
    <ToolContext.Provider value={{ selectedTool, setSelectedTool: handleSetTool, connectedTool, setConnectedTool, deviceStatus, setDeviceStatus }}>
      {children}
    </ToolContext.Provider>
  );
}

export const useSelectedTool = () => useContext(ToolContext);
